/**
 * Controls shared by the three bulk edit forms.
 *
 * Single responsibility of this module: hold the MIXED contract. MIXED is a
 * Symbol, and React throws "Cannot convert a Symbol value to a string" the
 * moment one reaches a DOM prop — so every control here translates it into
 * something renderable (an empty box with a hint, a disabled sentinel option, an
 * indeterminate checkbox) and no form ever forwards it itself.
 *
 * The other invariant: a control reports a value only when the user picks one.
 * The varies affordance is never submittable, so an untouched field cannot enter
 * the changeset.
 */

import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Button, Input } from "reactstrap";
import { MIXED, coerceEmptyToNull } from "../../shared/bulkFields";
import FieldRow from "./FieldRow";

// Shown when the selected entities disagree on a field's value: there is no
// common value to display, and typing one overwrites them all.
export const VARIES_LABEL = "— different values across selection —";

// Sentinel for the <option> that stands in for MIXED: a string, because option
// values must be strings, and one no real value can collide with.
const MIXED_OPTION_VALUE = "__MIXED__";

// Any scalar the model carries, or MIXED. A Symbol has no PropTypes primitive,
// and the controls' whole job is to accept it, so the check stays permissive.
const mixedValue = PropTypes.any;

function toNumber(value) {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
}

function fkValue(value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return value.id ?? "";
  return value;
}

/**
 * Checkbox over a selection: checked, unchecked, or indeterminate when the
 * selected entities disagree. The indeterminate state is a DOM property, not an
 * attribute, so it is set through a ref. Leaving it is one-way — there is no
 * value to go back to, so the per-field revert is the way out.
 */
export function TriStateCheckbox({ value, onChange, label }) {
  const ref = useRef(null);
  const isMixed = value === MIXED;

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = isMixed;
  }, [isMixed]);

  return (
    <div className="d-flex align-items-center" style={{ gap: 8 }}>
      <Input
        type="checkbox"
        innerRef={ref}
        aria-label={label}
        checked={isMixed ? false : !!value}
        onChange={() => onChange(isMixed ? true : !value)}
      />
      <span className={isMixed ? "text-muted font-italic" : undefined}>
        {isMixed ? VARIES_LABEL : (value && "on") || "off"}
      </span>
    </div>
  );
}

TriStateCheckbox.propTypes = {
  value: mixedValue,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
};

TriStateCheckbox.defaultProps = { value: null };

/**
 * Select over a selection. A divergent selection adds a disabled sentinel entry
 * that shows as selected, so the box never displays one entity's value as if it
 * were shared, and the sentinel can never be submitted.
 */
export function BulkSelect({ value, options, onChange, label, allowEmpty, emptyLabel }) {
  const isMixed = value === MIXED;
  const numericOptions = options.some((o) => typeof o.value === "number");

  const handleChange = (event) => {
    const raw = event.target.value;
    if (raw === MIXED_OPTION_VALUE) return;
    if (raw === "") {
      onChange(null);
      return;
    }
    onChange(numericOptions ? toNumber(raw) : raw);
  };

  return (
    <Input
      type="select"
      bsSize="sm"
      className="custom-select"
      aria-label={label}
      value={isMixed ? MIXED_OPTION_VALUE : fkValue(value)}
      onChange={handleChange}
    >
      {isMixed && (
        <option value={MIXED_OPTION_VALUE} disabled>
          {VARIES_LABEL}
        </option>
      )}
      {allowEmpty && <option value="">{emptyLabel}</option>}
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </Input>
  );
}

BulkSelect.propTypes = {
  value: mixedValue,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.node.isRequired,
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  allowEmpty: PropTypes.bool,
  emptyLabel: PropTypes.string,
};

BulkSelect.defaultProps = { value: null, allowEmpty: false, emptyLabel: "— none —" };

/**
 * Text or number input over a selection. A divergent selection shows an empty box
 * hinting that values vary; a blank edit is a real change and lands as null, the
 * same coercion the legacy recorder save path applies.
 */
export function BulkTextInput({ value, onChange, label, type, placeholder }) {
  const isMixed = value === MIXED;

  return (
    <Input
      bsSize="sm"
      type={type}
      aria-label={label}
      value={isMixed || value === null || value === undefined ? "" : value}
      placeholder={isMixed ? VARIES_LABEL : placeholder}
      onChange={(event) =>
        onChange(type === "number" ? toNumber(event.target.value) : coerceEmptyToNull(event.target.value))
      }
    />
  );
}

BulkTextInput.propTypes = {
  value: mixedValue,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  type: PropTypes.oneOf(["text", "number"]),
  placeholder: PropTypes.string,
};

BulkTextInput.defaultProps = { value: null, type: "text", placeholder: undefined };

/**
 * Header and footer shared by the three bulk forms: what the edit reaches, and
 * what is pending before the tree-wide Save All picks it up. The wording avoids
 * repeating the entity noun in both, so each line is uniquely identifiable.
 */
export function BulkFormHeader({ count, noun }) {
  return (
    <div className="mb-3">
      <h6 className="mb-0">
        ✎ Editing {count} {noun}
      </h6>
      <small className="text-muted">Changes join the current draft — nothing is sent until Save All.</small>
    </div>
  );
}

BulkFormHeader.propTypes = { count: PropTypes.number.isRequired, noun: PropTypes.string.isRequired };

export function BulkFormSummary({ dirtyCount, count }) {
  if (dirtyCount === 0) {
    return (
      <div className="text-muted mt-3" style={{ fontSize: "0.8rem" }}>
        No pending change on the {count} selected.
      </div>
    );
  }
  return (
    <div className="mt-3" style={{ fontSize: "0.8rem" }}>
      <strong>
        {dirtyCount} {dirtyCount === 1 ? "field" : "fields"} changed
      </strong>{" "}
      · applies to {count} selected
    </div>
  );
}

BulkFormSummary.propTypes = { dirtyCount: PropTypes.number.isRequired, count: PropTypes.number.isRequired };

/**
 * Alert-flag control over a selection. The single-view form already models these
 * flags as three states — null means "use the store default", true and false
 * override it — so in bulk they need a fourth, varies, and a checkbox cannot
 * carry that. Mirrors ViewForm's TriBool with the sentinel entry added.
 */
export function BulkTriBool({ value, onChange, label }) {
  const isMixed = value === MIXED;

  const handleChange = (event) => {
    const raw = event.target.value;
    if (raw === MIXED_OPTION_VALUE) return;
    if (raw === "") {
      onChange(null);
      return;
    }
    onChange(raw === "true");
  };

  const selected = () => {
    if (isMixed) return MIXED_OPTION_VALUE;
    if (value === null || value === undefined) return "";
    return String(value);
  };

  return (
    <Input
      type="select"
      bsSize="sm"
      className="custom-select"
      aria-label={label}
      value={selected()}
      onChange={handleChange}
      style={{ padding: "2px 6px", fontSize: "0.8rem" }}
    >
      {isMixed && (
        <option value={MIXED_OPTION_VALUE} disabled>
          {VARIES_LABEL}
        </option>
      )}
      <option value="">Default</option>
      <option value="true">Enabled</option>
      <option value="false">Disabled</option>
    </Input>
  );
}

BulkTriBool.propTypes = {
  value: mixedValue,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
};

BulkTriBool.defaultProps = { value: null };

/**
 * Field row that marks a pending change and offers to undo it. "Modified" means
 * at least one selected entity differs from its own server value, so reverting
 * sends each of them back to its own value — see bulkFields.dirtyFields.
 */
export function BulkFieldRow({ label, hint, modified, onRevert, selectedCount, children }) {
  const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  // Spell out the reach: reverting restores this field on EVERY selected entity, to
  // each one's own server value — including an edit made before bulk mode.
  const revertTitle = selectedCount
    ? `Revert this field on all ${selectedCount} selected, to each one's saved value`
    : "Revert this field";

  return (
    <FieldRow label={label} hint={hint}>
      <div
        data-testid={`bulk-field-${slug}`}
        data-modified={modified ? "true" : "false"}
        className="d-flex align-items-center"
        style={{
          gap: 8,
          background: modified ? "rgba(74, 134, 255, 0.10)" : undefined,
          borderRadius: 4,
          padding: modified ? 2 : undefined,
        }}
      >
        <div style={{ flex: 1 }}>{children}</div>
        {modified && <Button close aria-label={`Revert ${label}`} title={revertTitle} onClick={onRevert} />}
      </div>
    </FieldRow>
  );
}

BulkFieldRow.propTypes = {
  label: PropTypes.string.isRequired,
  hint: PropTypes.string,
  modified: PropTypes.bool,
  onRevert: PropTypes.func.isRequired,
  /** How many entities the revert reaches, for the affordance's title. */
  selectedCount: PropTypes.number,
  children: PropTypes.node.isRequired,
};

BulkFieldRow.defaultProps = { hint: null, modified: false, selectedCount: 0 };
